import { useEffect, useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { get211Data } from '../utils'
import { some } from 'lodash'
import usePagination from './usePagination'

/**
 * @typedef {Object} SearchParams
 * @property {string} term
 * @property {number} page
 * @property {string} sort
 * @property {number} lat
 * @property {number} lng
 * @property {number} distance
 * @property {string} serviceLanguage
 * @property {boolean} removeDigital
 */

/**
 * @typedef {import('@tanstack/react-query').DefinedUseQueryResult<import('../../definitions/Search211ApiTypes').Search211Response, unknown>} Result
 * @typedef {import('./usePagination').PaginationHook} PaginationMeta
 * @typedef {import('react').Dispatch<import('react').SetStateAction<string>>} SetSearchTerm
 * @typedef {import('react').Dispatch<import('react').SetStateAction<string>>} SetSort
 * @typedef {import('react').Dispatch<import('react').SetStateAction<number>>} SetDistance
 * @typedef {import('react').Dispatch<import('react').SetStateAction<string>>} SetServiceLanguage
 * @typedef {import('react').Dispatch<import('react').SetStateAction<boolean>>} SetRemoveDigital
 */

/**
 * @typedef {Object} CurrentSearch
 * @property {Result} searchQuery
 * @property {PaginationMeta} pagination
 * @property {function} fetchSearch
 * @property {SetSearchTerm} setSearchTerm
 * @property {SetSort} setSearchSort
 * @property {SetDistance} setSearchDistance
 * @property {SetServiceLanguage} setServiceLanguage
 * @property {SetRemoveDigital} setRemoveDigital
 * @property {Array<Object<string, any>>} prevSearches
 * @property {SearchParams} searchParams
 */

// eslint-disable-next-line valid-jsdoc
/**
 * Custom hook to search 211 API
 * @name use211Search
 * @param {string} key
 * @param {number} lat
 * @param {number} lng
 * @param {boolean=} cupwFilter
 * @param {number=} distance
 * @param {number=} pageSize
 * @param {number=} defaultPage
 * @param {string=} sort
 * @param {string=} serviceLang
 * @return {CurrentSearch}
 */
const use211Search = (key, lat, lng, cupwFilter = false, distance = 50, pageSize = 25, defaultPage = 0, sort = 'best', serviceLang = '') => {
  const [prevSearches, setPrevSearches] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [searchDistance, setSearchDistance] = useState(distance)
  const [searchSort, setSearchSort] = useState(sort)
  const [serviceLanguage, setServiceLanguage] = useState(serviceLang)
  const [searchPageIndex, setSearchPageIndex] = useState(defaultPage)
  const [removeDigital, setRemoveDigital] = useState(false)

  /** @type { import("../../definitions/Search211ApiTypes").Search211Response } */
  const initialSearchData = {
    RecordCount: 0,
    Records: [],
  }

  // const searchClient = useQueryClient()
  const searchQuery = useQuery(
    [key, { lat, lng, searchTerm, searchDistance, searchPageIndex, searchSort, removeDigital, serviceLanguage }],
    () =>
      get211Data(
        lat,
        lng,
        searchTerm,
        cupwFilter,
        searchPageIndex,
        searchDistance,
        serviceLanguage,
        searchSort,
        removeDigital
      ),
    {
      staleTime: 1000 * 60 * 5,
      initialData: initialSearchData,
      enabled: false,
    }
  )

  const { data: searchData } = searchQuery
  const resultsCount = searchData.RecordCount || 0
  const pagination = usePagination({ pageSize, resultsCount, defaultPage })
  const { pageIndex: resultsPageIndex } = pagination

  const searchParams = {
    term: searchTerm,
    page: searchPageIndex,
    sort: searchSort,
    lat,
    lng,
    distance: searchDistance,
    serviceLanguage,
    removeDigital,
  }

  const fetchSearch = useCallback(() => {
    if (
      searchParams.term &&
      searchParams.lat &&
      searchParams.lng &&
      !some(prevSearches, searchParams)
    ) {
      setPrevSearches((prevSearchesParams) => [...prevSearchesParams, searchParams])
      searchQuery
        .refetch()
        .then(() => console.log('Fetch search was executed with params:', searchParams))
    }
  }, [searchParams])

  // Fetch results when sort or current page changed.
  useEffect(() => {
    fetchSearch()
  }, [searchParams])

  useEffect(() => {
    // searchPageIndex = 0 means 1-10 result pages
    const convertedIndex = Math.floor(resultsPageIndex / 10)
    if (convertedIndex !== searchPageIndex) setSearchPageIndex(convertedIndex)
  }, [resultsPageIndex])

  return {
    searchQuery,
    pagination,
    prevSearches,
    fetchSearch,
    setSearchTerm,
    setSearchSort,
    setSearchDistance,
    setServiceLanguage,
    setRemoveDigital,
    searchParams,
  }
}

export default use211Search
